import { memo, ReactElement } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, InspectionTranslationKeys } from 'intl/keys';
import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { fieldLabelSx } from 'shared/styles/typography';
import { PROTOCOL_ITEM_ADD_ISSUE_QA } from './model';
import { ProtocolItemIssuesPresentationalProps } from './types';
import { CreateIssueDialog } from '../../../views/issue/wizard/createIssueDialog';
import { MemoIssueAddTile } from './IssueAddTile';
import {
  ISSUE_ADD_TILE_MAX_SIZE_PX,
  ISSUE_ADD_TILE_SIZE_PX,
  issuesListSx,
  protocolItemSectionSx,
  rootSx,
  secondaryColumnsSx,
} from './styles';

function ProtocolItemIssuesPresentational({
  disabled,
  disabledReason,
  issueCards,
  createInspectionIssue,
  processId,
  processCode,
  open,
  handleClose,
  initialValues,
  disabledFields,
  beforeSave,
  belowToolbar,
  attachmentsSection,
  alwaysShowSecondaryColumns = false,
}: ProtocolItemIssuesPresentationalProps): ReactElement {
  const intl = useIntl();
  // Compact when empty (like the attachments empty state), a full tile next
  // to the square issue tiles.
  const addTileSize =
    issueCards && issueCards.length > 0
      ? ISSUE_ADD_TILE_MAX_SIZE_PX
      : ISSUE_ADD_TILE_SIZE_PX;
  const showAttachmentsColumn =
    Boolean(attachmentsSection) || alwaysShowSecondaryColumns;

  const issuesLabel = formatTranslation(
    intl,
    InspectionTranslationKeys.itemIssuesSectionTitle
  );

  const createIssueLabel = formatTranslation(
    intl,
    CommonTranslationKeys.toolbarTitleCreateIssue
  );

  // Like the attachments column: add tile first, then the cards.
  const issuesSection = (
    <Box sx={protocolItemSectionSx}>
      <Typography component='p' sx={fieldLabelSx}>
        {issuesLabel}
      </Typography>
      <Box sx={issuesListSx} data-qa='protocol-item-issues-list'>
        <MemoIssueAddTile
          label={createIssueLabel}
          // The create dialog is modal; a second click while it opens is a
          // no-op at best and can re-open the missing-protocol warning.
          disabled={disabled || open}
          forbiddenReason={disabled ? disabledReason || undefined : undefined}
          size={addTileSize}
          onClick={createInspectionIssue}
          dataQa={PROTOCOL_ITEM_ADD_ISSUE_QA}
        />
        {issueCards}
      </Box>
    </Box>
  );

  return (
    <Box sx={rootSx}>
      {belowToolbar}
      <Box sx={secondaryColumnsSx}>
        {showAttachmentsColumn ? attachmentsSection : null}
        {issuesSection}
      </Box>

      <CreateIssueDialog
        beforeSave={beforeSave}
        processId={processId}
        processCode={processCode}
        initialValues={initialValues}
        disabledFields={disabledFields}
        afterDone={handleClose}
        open={open}
      />
    </Box>
  );
}

export const MemoProtocolItemIssuesPresentational = memo(
  ProtocolItemIssuesPresentational
);
